import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <div className="label-eyebrow mb-2">{label}</div>
      {children}
      {hint && <div className="mt-1.5 text-xs text-ink-soft">{hint}</div>}
    </label>
  );
}

export function TextInput({ testid, ...props }) {
  return (
    <input
      data-testid={testid}
      type="text"
      {...props}
      className={
        "h-14 w-full rounded-xl border border-line bg-white px-4 text-base text-ink outline-none transition-all placeholder:text-ink-soft/60 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 " +
        (props.className || "")
      }
    />
  );
}

export function NumberInput({ testid, ...props }) {
  return (
    <input
      data-testid={testid}
      type="number"
      inputMode="decimal"
      step="0.1"
      {...props}
      className={
        "h-14 w-full rounded-xl border border-line bg-white px-4 text-base text-ink outline-none transition-all placeholder:text-ink-soft/60 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 " +
        (props.className || "")
      }
    />
  );
}

export function PasswordInput({ testid, className = "", ...props }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        data-testid={testid}
        type={visible ? "text" : "password"}
        {...props}
        className={
          "h-12 w-full rounded-xl border border-line bg-muted px-4 pr-12 text-ink outline-none transition-all focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 " +
          className
        }
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Amagar contrasenya" : "Mostrar contrasenya"}
        className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-ink-soft transition-colors hover:bg-cream hover:text-ink"
      >
        {visible ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

export function TextArea({ testid, rows = 3, ...props }) {
  return (
    <textarea
      data-testid={testid}
      rows={rows}
      {...props}
      className={
        "w-full rounded-xl border border-line bg-white px-4 py-3 text-base text-ink outline-none transition-all placeholder:text-ink-soft/60 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 " +
        (props.className || "")
      }
    />
  );
}

export function Select({ testid, children, ...props }) {
  return (
    <select
      data-testid={testid}
      {...props}
      className={
        "h-14 w-full appearance-none rounded-xl border border-line bg-white bg-[length:14px] bg-[right_1rem_center] bg-no-repeat pl-4 pr-10 text-base text-ink outline-none transition-all focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 " +
        (props.className || "")
      }
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%23695D59'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z'/%3E%3C/svg%3E\")",
      }}
    >
      {children}
    </select>
  );
}

export function Button({ children, variant = "primary", testid, ...props }) {
  const styles = {
    primary:
      "bg-terracotta text-white hover:bg-terracotta-hover active:scale-[0.98]",
    secondary:
      "bg-cream text-ink hover:bg-[#E8D8D0] active:scale-[0.98]",
    ghost: "bg-transparent text-ink-soft hover:bg-muted",
    danger: "bg-ember text-white hover:bg-[#a02114] active:scale-[0.98]",
  };
  return (
    <button
      data-testid={testid}
      {...props}
      className={
        "inline-flex h-14 items-center justify-center gap-2 rounded-xl px-6 font-semibold tracking-wide transition-all disabled:cursor-not-allowed disabled:opacity-50 " +
        styles[variant] +
        " " +
        (props.className || "")
      }
    >
      {children}
    </button>
  );
}

export function Badge({ status }) {
  const ok = status === "OK";
  return (
    <span
      data-testid={`badge-${ok ? "ok" : "def"}`}
      className={
        "inline-flex items-center rounded-full px-3 py-1 text-xs font-bold tracking-wide " +
        (ok
          ? "bg-forest-bg text-forest"
          : "bg-ember-bg text-ember")
      }
    >
      {ok ? "OK" : "DEFICIÈNCIA"}
    </span>
  );
}

export function Card({ children, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={
        "rounded-2xl border border-line bg-surface p-5 shadow-card sm:p-6 " +
        className
      }
    >
      {children}
    </div>
  );
}
